import { useId, useState, type ReactNode } from 'react';
import { ChevronRightIcon } from 'lucide-react';

import { Button, buttonVariants } from '@/components/ui/button';
import {
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CopyButton } from '@/shared/components/CopyButton';
import { PendingRegion } from '@/shared/components/PendingRegion';
import { SettingsPanel } from '@/shared/components/SettingsPanel';

import { EDITOR_COPY, MCP_CLIENT_ID } from '../copy';
import { useMcpDiscovery } from '../discovery';
import {
  AI_TOOLS,
  claudeAiConnectorLink,
  claudeCodeCommand,
  codexCommand,
  copilotConfig,
  editorConfig,
  reachableFromClaudeAi,
  tryItems,
  vsCodeInstallLink,
  type AiTool,
} from '../setups';
import { CopyBlock } from './CopyBlock';
import { ToolMark } from './ToolMark';
import { YourAiTools } from './YourAiTools';

/**
 * How to connect an AI tool to this app (specs 236, 241, 242): a setup per
 * tool, one tab each, as numbered steps that end in something to try — or,
 * where the server is switched off, a plain statement of that and nothing to
 * copy.
 */
export function ConnectEditorPage({
  tool,
  onToolChange,
}: {
  /** The tab the address names (`?tool=`), so a setup can be linked to. */
  tool?: AiTool;
  onToolChange?: (tool: AiTool) => void;
} = {}) {
  const discovery = useMcpDiscovery();

  return (
    <div className="mx-auto flex w-full max-w-[44rem] flex-col gap-6 p-4 md:p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-foreground text-xl font-semibold">
          {EDITOR_COPY.title}
        </h1>
        <p className="text-muted-foreground text-sm">{EDITOR_COPY.intro}</p>
      </header>

      {discovery.isPending ? (
        <PendingRegion label={EDITOR_COPY.loading} className="gap-6">
          {/* The cards' own frame, so the wait has the page's shape. */}
          {[0, 1].map((card) => (
            <div
              key={card}
              className="border-border bg-card flex flex-col gap-3 rounded-xl border p-4 sm:p-5"
            >
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </PendingRegion>
      ) : discovery.isError ? (
        <div className="flex flex-col items-center gap-2 py-6">
          <p className="text-muted-foreground text-sm">
            {EDITOR_COPY.checkFailed}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void discovery.refetch();
            }}
          >
            {EDITOR_COPY.retry}
          </Button>
        </div>
      ) : discovery.data.status === 'off' ? (
        <Panel id="editor-off" heading={EDITOR_COPY.offHeading}>
          <p className="text-muted-foreground text-sm">{EDITOR_COPY.off}</p>
        </Panel>
      ) : (
        <>
          <YourAiTools />
          <Setups
            address={discovery.data.address}
            tool={tool}
            onToolChange={onToolChange}
          />
        </>
      )}
    </div>
  );
}

function Setups({
  address,
  tool,
  onToolChange,
}: {
  address: string;
  tool?: AiTool;
  onToolChange?: (tool: AiTool) => void;
}) {
  const [chosen, setChosen] = useState<AiTool>('claude-code');
  const selected = tool ?? chosen;
  const panelId = useId();

  return (
    <>
      {/* The setup leads (spec 242): it is what a person came for, and every
          setup already holds the address, which closes the card below it. */}
      <Panel id="editor-setup" heading={EDITOR_COPY.setupHeading}>
        <TabsList
          aria-label={EDITOR_COPY.toolsLabel}
          className="border-border border-b"
        >
          {AI_TOOLS.map((candidate) => (
            <TabsTrigger
              key={candidate}
              id={`${panelId}-tab-${candidate}`}
              panelId={panelId}
              selected={selected === candidate}
              className="flex items-center gap-1.5"
              onClick={() => {
                setChosen(candidate);
                onToolChange?.(candidate);
              }}
            >
              <ToolMark tool={candidate} className="size-4 shrink-0" />
              {EDITOR_COPY.tabs[candidate]}
            </TabsTrigger>
          ))}
        </TabsList>
        <div
          id={panelId}
          role="tabpanel"
          aria-labelledby={`${panelId}-tab-${selected}`}
          className="flex flex-col gap-4"
        >
          <Setup tool={selected} address={address} />
        </div>
        <div className="border-border flex flex-col gap-2 border-t pt-4">
          <h3 className="text-foreground text-sm font-medium">
            {EDITOR_COPY.addressHeading}
          </h3>
          <Note>{EDITOR_COPY.addressHint}</Note>
          <CopyBlock text={address} label="the address" />
        </div>
      </Panel>

      <Panel id="editor-expect" heading={EDITOR_COPY.expectHeading}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <h3 className="text-foreground text-sm font-medium">
              {EDITOR_COPY.canHeading}
            </h3>
            <List items={EDITOR_COPY.can} />
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="text-foreground text-sm font-medium">
              {EDITOR_COPY.rulesHeading}
            </h3>
            <List items={EDITOR_COPY.rules} />
          </div>
        </div>
      </Panel>
    </>
  );
}

/** One tool's setup: add it, sign in, then something to try. */
function Setup({ tool, address }: { tool: AiTool; address: string }) {
  const tryIt = (
    <Step title={EDITOR_COPY.steps.tryIt}>
      <TryIt tool={tool} />
    </Step>
  );

  switch (tool) {
    case 'claude-code':
      return (
        <Steps>
          <Step title={EDITOR_COPY.steps.add}>
            <Hint>{EDITOR_COPY.claudeAdd}</Hint>
            <CopyBlock
              text={claudeCodeCommand(address)}
              label="the Claude Code command"
            />
          </Step>
          <Step title={EDITOR_COPY.steps.signIn}>
            <Hint>{EDITOR_COPY.claudeSignIn}</Hint>
          </Step>
          {tryIt}
        </Steps>
      );
    case 'copilot':
      return (
        <>
          <Steps>
            <Step title={EDITOR_COPY.steps.add}>
              <Actions>
                {/* An app link, not a page: it opens VS Code, so no new tab. */}
                <a href={vsCodeInstallLink(address)} className={primaryClass}>
                  {EDITOR_COPY.vsCodeInstall}
                </a>
                <a
                  href={vsCodeInstallLink(address, true)}
                  className={secondaryClass}
                >
                  {EDITOR_COPY.vsCodeInsidersInstall}
                </a>
              </Actions>
              <Hint>{EDITOR_COPY.vsCodeCheck(address)}</Hint>
              <ByHand label={EDITOR_COPY.copilotByHand}>
                <Hint>{EDITOR_COPY.copilotHint}</Hint>
                <CopyBlock
                  text={copilotConfig(address)}
                  label="the Copilot config"
                />
                <Hint>{EDITOR_COPY.copilotCliSignIn}</Hint>
              </ByHand>
            </Step>
            <Step title={EDITOR_COPY.steps.signIn}>
              <Hint>{EDITOR_COPY.copilotSignIn}</Hint>
            </Step>
            {tryIt}
          </Steps>
          <Note>{EDITOR_COPY.copilotPolicy}</Note>
        </>
      );
    case 'codex':
      return (
        <>
          <Steps>
            <Step title={EDITOR_COPY.steps.add}>
              <Hint>{EDITOR_COPY.codexAdd}</Hint>
              <CopyBlock
                text={codexCommand(address)}
                label="the Codex command"
              />
            </Step>
            <Step title={EDITOR_COPY.steps.signIn}>
              <Hint>{EDITOR_COPY.codexSignIn}</Hint>
            </Step>
            {tryIt}
          </Steps>
          <Note>{EDITOR_COPY.codexShared}</Note>
        </>
      );
    case 'claude-ai':
      // Offered only where it could ever connect (spec 241 Q4); elsewhere the
      // tab says why rather than vanishing (spec 242).
      if (!reachableFromClaudeAi(address)) {
        return <Hint>{EDITOR_COPY.claudeAiUnreachable(address)}</Hint>;
      }
      return (
        <>
          <Hint>{EDITOR_COPY.claudeAiWho}</Hint>
          <Steps>
            <Step title={EDITOR_COPY.steps.add}>
              <Actions>
                <a
                  href={claudeAiConnectorLink(address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={primaryClass}
                >
                  {EDITOR_COPY.claudeAiOpen}
                </a>
                <a
                  href={claudeAiConnectorLink(address, true)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={secondaryClass}
                >
                  {EDITOR_COPY.claudeAiOpenForOrganization}
                </a>
              </Actions>
              <Hint>{EDITOR_COPY.claudeAiCheck(address)}</Hint>
            </Step>
            <Step title={EDITOR_COPY.steps.clientId}>
              <Hint>{EDITOR_COPY.claudeAiClientId}</Hint>
              <CopyBlock text={MCP_CLIENT_ID} label="the client ID" />
            </Step>
            <Step title={EDITOR_COPY.steps.signIn}>
              <Hint>{EDITOR_COPY.claudeAiSignIn}</Hint>
            </Step>
            {tryIt}
          </Steps>
          <Note>{EDITOR_COPY.claudeAiReach}</Note>
        </>
      );
    case 'other':
      return (
        <>
          <Steps>
            <Step title={EDITOR_COPY.steps.add}>
              <Hint>{EDITOR_COPY.otherAdd}</Hint>
              <CopyBlock
                text={editorConfig(address)}
                label="the editor config"
              />
            </Step>
            <Step title={EDITOR_COPY.steps.signIn}>
              <Hint>{EDITOR_COPY.otherSignIn(MCP_CLIENT_ID)}</Hint>
            </Step>
            {tryIt}
          </Steps>
          <Note>{EDITOR_COPY.otherUntried}</Note>
        </>
      );
  }
}

/** The payoff (spec 242): the first things to type once the tool is in. */
function TryIt({ tool }: { tool: AiTool }) {
  const lead =
    tool === 'claude-code'
      ? EDITOR_COPY.tryCommands
      : tool === 'copilot'
        ? EDITOR_COPY.tryCopilot
        : EDITOR_COPY.tryWords;

  return (
    <>
      <Hint>{lead}</Hint>
      <ul className="flex flex-col gap-1">
        {tryItems(tool).map((item) => (
          <li key={item.text} className="flex items-center gap-2">
            <span className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
              <code className="text-foreground font-mono text-xs [overflow-wrap:anywhere]">
                {item.text}
              </code>
              {item.what ? (
                <span className="text-muted-foreground text-xs">
                  {item.what}
                </span>
              ) : null}
            </span>
            <CopyButton
              text={item.text}
              label={EDITOR_COPY.copy(item.text)}
              copiedLabel={EDITOR_COPY.copied(item.text)}
              failedLabel={EDITOR_COPY.copyFailed(item.text)}
              className="shrink-0"
              announce
            />
          </li>
        ))}
      </ul>
    </>
  );
}

const primaryClass = buttonVariants({ variant: 'default' });
const secondaryClass = buttonVariants({ variant: 'outline' });

/** The steps of one setup, numbered as the person follows them. */
function Steps({ children }: { children: ReactNode }) {
  return (
    <ol className="flex flex-col gap-5 [counter-reset:step]">{children}</ol>
  );
}

/**
 * One step. The number is drawn, not read: the list already tells a screen
 * reader where it is, and the title says what the step is for.
 */
function Step({ title, children }: { title: string; children: ReactNode }) {
  return (
    <li className="flex gap-3 [counter-increment:step]">
      <span
        aria-hidden="true"
        className="bg-muted text-foreground flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium tabular-nums before:content-[counter(step)]"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2 pt-0.5">
        <h3 className="text-foreground text-sm font-medium">{title}</h3>
        {children}
      </div>
    </li>
  );
}

/** The longer way round, kept out of sight until someone wants it. */
function ByHand({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Collapsible>
      <CollapsibleTrigger className="text-muted-foreground hover:text-foreground group touch-target flex items-center gap-1 text-sm">
        <ChevronRightIcon
          aria-hidden="true"
          className="size-4 shrink-0 transition-transform duration-[var(--motion-duration-base)] group-data-panel-open:rotate-90"
        />
        {label}
      </CollapsibleTrigger>
      <CollapsiblePanel className="flex flex-col gap-2 pt-2">
        {children}
      </CollapsiblePanel>
    </Collapsible>
  );
}

function Actions({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-2">{children}</div>;
}

function Hint({ children }: { children: ReactNode }) {
  return <p className="text-muted-foreground text-sm">{children}</p>;
}

/** Fine print under a setup: true for some people, not a step for all. */
function Note({ children }: { children: ReactNode }) {
  return <p className="text-muted-foreground text-xs">{children}</p>;
}

function Panel({
  id,
  heading,
  children,
}: {
  id: string;
  heading: string;
  children: ReactNode;
}) {
  return (
    <SettingsPanel labelledBy={id}>
      <h2 id={id} className="text-foreground text-sm font-medium">
        {heading}
      </h2>
      {children}
    </SettingsPanel>
  );
}

function List({ items }: { items: readonly string[] }) {
  return (
    <ul className="text-muted-foreground flex list-disc flex-col gap-1 ps-5 text-sm">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}
